import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AdminIcon,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Modal,
  Note,
  type BadgeTone,
} from '../admin-kit';

/**
 * Wat ploegoverdracht is, in het scherm zelf (FBK-10).
 *
 * Een tester vroeg "wat is de bedoeling van die ploegoverdracht en machinestatus?". Het scherm toonde
 * alleen instellingen (ploegen, uren, machines), en de overdracht zelf staat in de app van de
 * werkvloer, op een uur dat een beheerder overdag zelden ziet. Deze kaart legt het uit, en
 * "Voorbeeld bekijken" toont een overdracht zoals de volgende ploeg ze krijgt, los van het uur.
 *
 * Inklapbaar en onthouden per toestel: wie het weet, wil de kaart niet elke keer zien.
 */

const KEY = 'up2d8.handover-explainer';

function remembered(): boolean {
  try {
    return window.localStorage.getItem(KEY) !== 'closed';
  } catch {
    return true;
  }
}

function remember(open: boolean): void {
  try {
    window.localStorage.setItem(KEY, open ? 'open' : 'closed');
  } catch {
    // Geen opslag: de kaart staat dan gewoon open.
  }
}

const STATUS: { key: 'running' | 'fault' | 'stopped'; tone: BadgeTone }[] = [
  { key: 'running', tone: 'ok' },
  { key: 'fault', tone: 'attention' },
  { key: 'stopped', tone: 'danger' },
];

export function HandoverExplainer() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(remembered);
  const [example, setExample] = useState(false);

  const toggle = () => {
    setOpen(!open);
    remember(!open);
  };

  return (
    <Card testId="handover-explainer">
      <CardHead
        title={t('handoverExplainer.title')}
        actions={
          <Btn variant="ghost" size="sm" onClick={toggle} aria-expanded={open}>
            {open ? t('handoverExplainer.hide') : t('handoverExplainer.show')}
          </Btn>
        }
      />
      {open ? (
        <CardPad>
          <p style={{ margin: 0 }}>{t('handoverExplainer.what')}</p>

          <ol className="c-handover-flow" aria-label={t('handoverExplainer.flowLabel')}>
            {(['early', 'late', 'night'] as const).map((shift, index) => (
              <li key={shift}>
                <strong>{t(`handoverExplainer.shift.${shift}`)}</strong>
                <span className="a-muted">{t(`handoverExplainer.shiftHint.${shift}`)}</span>
                {index < 2 ? (
                  <AdminIcon name="chevronRight" size={16} className="c-handover-arrow" />
                ) : null}
              </li>
            ))}
          </ol>

          <div className="a-label" style={{ marginTop: 12 }}>
            {t('handoverExplainer.statusTitle')}
          </div>
          <ul className="c-handover-status">
            {STATUS.map(({ key, tone }) => (
              <li key={key}>
                <Badge tone={tone}>{t(`handoverExplainer.status.${key}`)}</Badge>
                <span>{t(`handoverExplainer.statusHint.${key}`)}</span>
              </li>
            ))}
          </ul>

          <Note tone="info">{t('handoverExplainer.openItems')}</Note>

          <div style={{ marginTop: 12 }}>
            <Btn
              variant="sec"
              size="sm"
              icon={<AdminIcon name="eye" />}
              testId="handover-example"
              onClick={() => setExample(true)}
            >
              {t('handoverExplainer.example')}
            </Btn>
          </div>
        </CardPad>
      ) : null}

      <Modal
        open={example}
        onClose={() => setExample(false)}
        title={t('handoverExplainer.exampleTitle')}
        sub={t('handoverExplainer.exampleSub')}
        testId="handover-example-modal"
        footer={
          <Btn variant="sec" onClick={() => setExample(false)}>
            {t('common.close')}
          </Btn>
        }
      >
        <div className="c-handover-example">
          <div className="a-meta">{t('handoverExplainer.sample.from')}</div>
          <ul className="c-handover-status">
            <li>
              <Badge tone="ok">{t('handoverExplainer.status.running')}</Badge>
              <span>W1 · {t('handoverExplainer.sample.w1')}</span>
            </li>
            <li>
              <Badge tone="danger">{t('handoverExplainer.status.stopped')}</Badge>
              <span>W2 · {t('handoverExplainer.sample.w2')}</span>
            </li>
            <li>
              <Badge tone="attention">{t('handoverExplainer.status.fault')}</Badge>
              <span>W3 · {t('handoverExplainer.sample.w3')}</span>
            </li>
          </ul>
          <div className="a-label">{t('handoverExplainer.sample.openTitle')}</div>
          <ul style={{ margin: '4px 0 12px', paddingLeft: 18 }}>
            <li>{t('handoverExplainer.sample.open1')}</li>
            <li>{t('handoverExplainer.sample.open2')}</li>
          </ul>
          <div className="a-label">{t('handoverExplainer.sample.noteTitle')}</div>
          <p style={{ marginTop: 4 }}>{t('handoverExplainer.sample.note')}</p>
          <Note tone="info">{t('handoverExplainer.sample.read')}</Note>
        </div>
      </Modal>
    </Card>
  );
}
